import { useState, useCallback, useRef } from 'react';
import { VoiceAudioParams, VoiceProfile } from '../types';

const VOICE_PROFILES: VoiceProfile[] = [
  { id: 'melodic', name: 'Melodic', description: 'Warm and flowing, like a lullaby', icon: '🎵' },
  { id: 'dreamy', name: 'Dreamy', description: 'Soft and ethereal, half-remembered', icon: '🌙' },
  { id: 'playful', name: 'Playful', description: 'Light and curious, with a smile', icon: '🍃' },
  { id: 'healing', name: 'Healing', description: 'Gentle and grounding, like rain', icon: '💧' },
];

export function useVoice() {
  const [activeProfile, setActiveProfile] = useState<VoiceProfile['id']>('melodic');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const speak = useCallback(
    (text: string, params?: VoiceAudioParams) => {
      if (!window.speechSynthesis) return;
      window.speechSynthesis.cancel();

      const profile = params?.profile || activeProfile;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = params?.speed ?? 0.92;
      utterance.pitch = params?.pitch ?? 1.0;
      utterance.volume = 0.9;

      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find((v) =>
        profile === 'melodic'
          ? v.name.includes('Samantha') || v.name.includes('Google UK English Female')
          : profile === 'dreamy'
            ? v.name.includes('Google US English') || v.name.includes('Karen')
            : profile === 'playful'
              ? v.name.includes('Tessa') || v.name.includes('Google UK English')
              : v.name.includes('Google US English') || v.name.includes('Samantha')
      );
      if (preferredVoice) utterance.voice = preferredVoice;

      utterance.onstart = () => {
        setIsSpeaking(true);
        setIsPlaying(true);
      };
      utterance.onend = () => {
        setIsSpeaking(false);
        setIsPlaying(false);
      };
      utterance.onerror = () => {
        setIsSpeaking(false);
        setIsPlaying(false);
      };

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    [activeProfile]
  );

  const stop = useCallback(() => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
    setIsPlaying(false);
  }, []);

  const toggleProfile = useCallback(() => {
    setActiveProfile((prev) => {
      const idx = VOICE_PROFILES.findIndex((p) => p.id === prev);
      return VOICE_PROFILES[(idx + 1) % VOICE_PROFILES.length].id;
    });
  }, []);

  const currentProfile = VOICE_PROFILES.find((p) => p.id === activeProfile) || VOICE_PROFILES[0];

  return {
    profiles: VOICE_PROFILES,
    activeProfile,
    currentProfile,
    setActiveProfile,
    toggleProfile,
    speak,
    stop,
    isSpeaking,
    isPlaying,
  };
}
